import { useCallback, type ChangeEvent, type ComponentProps } from "react";
import {
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox as MUICheckbox,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type { TranslationKeyAndOptions } from "@thalamos/vision-common";
import { translate } from "@thalamos/vision-common";

import { Translation } from "../../Translation/Translation.js";
import { FormError } from "../FormError/FormError.js";

const debug = debugFn("vision-frontend:Checkbox");

export type CheckboxProps = {
  fieldName: string;
  label: TranslationKeyAndOptions;
  isEditable?: boolean;
} & Pick<ComponentProps<typeof MUICheckbox>, "autoFocus">;

type CheckboxOutput = { value: "yes"; valueLabel: string } | "";

export const Checkbox = ({
  fieldName,
  label,
  autoFocus,
  isEditable = true,
}: CheckboxProps) => {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField<CheckboxOutput>(fieldName);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const runAsync = async () => {
        const newValue = event.target.checked
          ? ({
              value: "yes",
              valueLabel: translate(t, label),
            } as const)
          : "";

        debug("Setting value", {
          newValue,
        });
        await helpers.setValue(newValue);
        await helpers.setTouched(true);
      };

      void runAsync();
    },
    [helpers, label, t],
  );

  return (
    <FormControl
      error={!!(meta.touched && meta.error)}
      component="fieldset"
      data-testid={`formField-${fieldName}`}
    >
      <FormControlLabel
        disabled={!isEditable}
        control={
          <MUICheckbox
            checked={field.value !== ""}
            onChange={handleChange}
            name={field.name}
            disabled={!isEditable}
            autoFocus={autoFocus}
          />
        }
        label={<Translation {...label} />}
      />
      {meta.touched && meta.error && <FormError error={meta.error} />}
      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(field.value)}
          </Typography>
          <Divider />
        </>
      )}
    </FormControl>
  );
};
